/**
 * The files behind a project, in playing order (Dax, 2026-10-05): when each was recorded, its length, size
 * and shape, so the order can be checked against the camera's own clock, and the original one click away.
 * Drag to reorder, or order by recording time; a new order analyses the clip again. A file that does not
 * belong moves out as a clip of its own, or (an owner) is removed for good.
 */
import { useMutation, useQuery } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Icon } from '@/components/icons';
import { RowMenu } from '@/components/row-menu';
import { fmtBytes, fmtTime } from '@/lib/format';
import { queryClient, trpc } from '@/lib/trpc';

/** When a file was recorded, to the second: two files of one shoot are often a minute apart. */
function recorded(iso: string | null): string {
  if (!iso) return 'recording time unknown';
  return new Date(iso).toLocaleString(undefined, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' });
}

export function PartsModal({ assetId, canRemove = false, onClose }: { assetId: string; canRemove?: boolean; onClose: () => void }) {
  const detail = useQuery(trpc.assets.detail.queryOptions({ assetId }));
  const [order, setOrder] = useState<string[] | null>(null);
  const [drag, setDrag] = useState<string | null>(null);
  const [removing, setRemoving] = useState<string | null>(null);
  useEffect(() => {
    if (detail.data && !order) setOrder(detail.data.parts.map((p) => p.id));
  }, [detail.data, order]);
  const done = async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: trpc.assets.pathKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.library.pathKey() }),
      queryClient.invalidateQueries({ queryKey: trpc.upload.mine.queryKey() }),
    ]);
    onClose();
  };
  const reorder = useMutation(trpc.assets.reorderParts.mutationOptions({ onSuccess: done }));
  const eject = useMutation(trpc.assets.ejectPart.mutationOptions({ onSuccess: done }));
  const removePart = useMutation(trpc.assets.removePart.mutationOptions({ onSuccess: done }));
  const partUrl = useMutation(trpc.assets.partUrl.mutationOptions());
  const parts = detail.data?.parts ?? [];
  const byId = new Map(parts.map((p) => [p.id, p]));
  const shown = (order ?? []).map((id) => byId.get(id)).filter((p): p is NonNullable<typeof p> => Boolean(p));
  const changed = order !== null && detail.data !== undefined && order.join() !== detail.data.parts.map((p) => p.id).join();
  const byRecording = [...parts].filter((p) => p.recordedAt).sort((a, b) => a.recordedAt!.localeCompare(b.recordedAt!)).map((p) => p.id);
  const busy = reorder.isPending || eject.isPending || removePart.isPending;
  const failure = reorder.error ?? eject.error ?? removePart.error ?? partUrl.error;
  /** The original opens in a new tab; the tab is opened on the click itself so no popup blocker stops it. */
  const openOriginal = async (partId: string) => {
    const tab = window.open('', '_blank');
    try {
      const { url } = await partUrl.mutateAsync({ assetId, partId });
      if (tab) tab.location.href = url;
    } catch {
      tab?.close();
    }
  };
  return (
    <>
      <button type="button" className="overlay overlay-btn" aria-label="Close" onClick={onClose} />
      <div className="modal parts-modal" role="dialog" aria-label="Parts of this clip">
        <h2>Parts</h2>
        <span className="sub">{detail.data?.partsOrder === 'user' ? 'In the order you set.' : 'In the order the files were recorded, checked against the picture at each join.'} Drag to change it; a new order analyses the clip again.</span>
        <div className="picker-list">
          {shown.map((p, i) => (
            <div key={p.id} className={`row part-row${drag === p.id ? ' dragging' : ''}`} draggable
              onDragStart={() => setDrag(p.id)}
              onDragOver={(e) => {
                e.preventDefault();
                if (!drag || drag === p.id || !order) return;
                const next = order.filter((id) => id !== drag);
                next.splice(next.indexOf(p.id), 0, drag);
                setOrder(next);
              }}
              onDragEnd={() => setDrag(null)}>
              <span className="facet-grip" aria-hidden="true"><Icon name="grip" /></span>
              <span className="mono sub part-idx">{i + 1}</span>
              <span className="part-main">
                <b className="part-name">{p.name}</b>
                <span className="sub">
                  {recorded(p.recordedAt)}
                  {p.durationMs !== null ? ` · ${fmtTime(p.durationMs, false)}` : ''}
                  {p.bytes !== null ? ` · ${fmtBytes(p.bytes)}` : ''}
                  {p.width && p.height ? ` · ${p.width}×${p.height}` : ''}
                </span>
              </span>
              {p.offsetMs !== null && <span className="chip mono time-pill">from {fmtTime(p.offsetMs, false)}</span>}
              {removing === p.id ? (
                <span className="row part-confirm">
                  <button type="button" className="btn quiet" onClick={() => setRemoving(null)}>Keep</button>
                  <button type="button" className="btn danger" disabled={busy} onClick={() => removePart.mutate({ assetId, partId: p.id })}>Remove</button>
                </span>
              ) : (
                <RowMenu title="File actions" items={[
                  { label: 'Open the original', onClick: () => void openOriginal(p.id) },
                  null,
                  { label: 'Move out as its own clip', onClick: () => eject.mutate({ assetId, partId: p.id }), disabled: busy || parts.length < 2 },
                  ...(canRemove ? [{ label: 'Remove from this project', danger: true, onClick: () => setRemoving(p.id), disabled: busy || parts.length < 2 }] : []),
                ]} />
              )}
            </div>
          ))}
          {detail.isPending && <div className="empty"><span className="spinner" aria-label="Loading" /></div>}
        </div>
        {failure && <div className="error" role="alert">{failure.message}</div>}
        <div className="row between">
          <button type="button" className="btn quiet" onClick={onClose}>Cancel</button>
          <span className="row">
            <button type="button" className="btn" disabled={busy || byRecording.length !== parts.length || (order ?? []).join() === byRecording.join()}
              onClick={() => setOrder(byRecording)}>Order by recording time</button>
            <button type="button" className="btn primary" disabled={!changed || busy || !order}
              onClick={() => order && reorder.mutate({ assetId, partIds: order })}>
              {reorder.isPending ? 'Saving…' : 'Save order and analyse again'}
            </button>
          </span>
        </div>
      </div>
    </>
  );
}
